import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import type { FC } from "react";
import { useEffect, useState } from "react";
import ImagesPicker from "../../components/imagesPicker/ImagesPicker";
import { useImages } from "../../media/useImages";
import AgentSelect from "../../sharedComponents/agentSelect/AgentSelect";
import type { Region, RegionImage } from "./regionsApi";
import { useTripTags } from "./useTripTags";

export interface RegionFormValues {
  readonly name: string;
  readonly description: string | null;
  readonly countries: string[];
  readonly image: RegionImage | null;
  readonly responsibleAgentId: string | null;
}

interface Props {
  readonly open: boolean;
  readonly region?: Region;
  readonly onClose: () => void;
  readonly onSave: (values: RegionFormValues) => Promise<void>;
}

const EditRegionDialog: FC<Props> = ({ open, region, onClose, onSave }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [countries, setCountries] = useState<string[]>([]);
  const [image, setImage] = useState<RegionImage | null>(null);
  const [responsibleAgentId, setResponsibleAgentId] = useState<string | null>(null);
  const { tags } = useTripTags();
  const { images, isLoading: imagesLoading } = useImages();

  const countryTags = tags.filter((t) => t.category === "Kraj");

  useEffect(() => {
    if (open) {
      setName(region?.name ?? "");
      setDescription(region?.description ?? "");
      setCountries(region?.countries ?? []);
      setImage(region?.image ?? null);
      setResponsibleAgentId(region?.responsibleAgentId ?? null);
    }
  }, [open, region]);

  const handleSave = async () => {
    await onSave({ name, description: description || null, countries, image, responsibleAgentId });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{region ? "Edytuj region" : "Dodaj region"}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Id"
          fullWidth
          disabled={true}
          value={region?.id.userFriendlyId ?? "Do wygenerowania"}
        />
        <TextField
          autoFocus
          margin="dense"
          label="Nazwa"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <TextField
          margin="dense"
          label="Opis"
          fullWidth
          multiline
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <FormControl fullWidth margin="dense">
          <InputLabel id="countries-label">Kraje</InputLabel>
          <Select
            labelId="countries-label"
            id="countries-select"
            multiple
            value={countries}
            label="Kraje"
            onChange={(e) => {
              const value = e.target.value;
              setCountries(typeof value === "string" ? value.split(",") : value);
            }}
          >
            {countryTags.map((tag) => (
              <MenuItem key={tag.id} value={tag.name}>
                {tag.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth margin="dense">
          <AgentSelect
            label="Osoba odpowiedzialna"
            agentId={responsibleAgentId}
            emptyOptionLabel="Brak"
            onChange={setResponsibleAgentId}
          />
        </FormControl>
        <ImagesPicker
          label="Zdjęcie"
          limit={1}
          images={images}
          isLoading={imagesLoading}
          selectedImages={image ? [image] : []}
          onSelectionChange={(images) => {
            if (images.length === 0) {
              setImage(null);
            } else {
              setImage(images[0]);
            }
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Anuluj</Button>
        <Button onClick={handleSave} disabled={!name}>
          Zapisz
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditRegionDialog;
